/**
 * wsCase store — v0.9.0.
 *
 * Reactive case-status overlay for the officer portal. Owns the
 * pending-decisions queue that PendingDecisionsView renders (list is
 * REST-hydrated on mount; then WS events add/remove rows live).
 *
 * WS events dispatched from useDarsEvents:
 *   - case.pending_decision  → add case to pending list (if assigned officer)
 *   - case.decided           → remove case from pending list
 *   - case.reopened          → remove case from pending list (moved back to in_progress)
 *
 * Backend fan-out publishes these to `dars:case:{case_id}` +
 * `dars:officer:{assigned_officer_id}` — see audit_logger._WS_FAN_OUT_MAP.
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { OfficerCase } from '@/services/officerCasesService'

export const useWsCaseStore = defineStore('wsCase', () => {
  // The pending-decisions queue — full case rows so we can render the
  // list without a REST re-fetch when a case joins the queue.
  const pendingCases = ref<OfficerCase[]>([])

  // Loading + error flags — shared across list views so they can be
  // updated by whichever consumer is fetching. Kept here so the store
  // survives view remounts without losing state.
  const loading = ref(false)
  const error = ref('')

  // Simple map: case_id → latest status seen over WS. Views that want
  // to badge a case with "pending decision" can look here.
  const statusByCase = ref<Record<string, string>>({})

  function setPending(rows: OfficerCase[]) {
    pendingCases.value = rows
    for (const r of rows) {
      statusByCase.value[r.id] = 'pending_decision'
    }
  }

  function addPending(c: OfficerCase) {
    // De-dupe by id — WS events can fire multiple times per lifecycle.
    if (pendingCases.value.some(row => row.id === c.id)) return
    pendingCases.value = [c, ...pendingCases.value]
    statusByCase.value[c.id] = 'pending_decision'
  }

  function removePending(caseId: string) {
    pendingCases.value = pendingCases.value.filter(c => c.id !== caseId)
  }

  function setStatus(caseId: string, status: string) {
    statusByCase.value[caseId] = status
    if (status !== 'pending_decision') {
      removePending(caseId)
    }
  }

  function reset() {
    pendingCases.value = []
    statusByCase.value = {}
    error.value = ''
    loading.value = false
  }

  return {
    pendingCases,
    loading,
    error,
    statusByCase,
    setPending,
    addPending,
    removePending,
    setStatus,
    reset,
  }
})
